/**
 * A project's area on the globe: the rings of its block boundaries, else of its signed contract's survey areas, and
 * their box. The Operations Hub draws these outlines (`hubProject`, `contractAreaFeatures`), and Teslyn Cloud asks
 * for public air traffic around the same box (`cloud-traffic.ts`), so both read the area the same way.
 *
 * Kept free of the map and quote modules (only `map-geo.js` at run time), so the Edge Functions' build of
 * `cloud-traffic.ts` (tools/dev/copy-edge-shared.mjs) stays small.
 */
import { boundsOf, projectedToLatLng, utmToLatLng, type GeoBounds } from './map-geo.js';
import type { LatLng } from './planning.js';

/** What a block needs for its ring: the boundary in its projection (the block's EPSG, else the project's). */
export interface AreaBlock {
  readonly epsg?: number | null;
  readonly boundary?: ReadonlyArray<readonly [number, number]> | null;
  readonly deleted_at?: string | null;
}

/** What a contract area needs for its ring (the quote snapshot's area: grid boundary, else its printed table). */
export interface AreaShape {
  readonly zone?: number | null;
  readonly hemisphere?: 'N' | 'S' | null;
  readonly boundary?: ReadonlyArray<readonly [number, number]> | null;
  readonly boundary_table?: {
    readonly kind: 'grid' | 'geographic';
    readonly zone: number | null;
    readonly points: ReadonlyArray<readonly [number, number]>;
  } | null;
}

/** A block's boundary in WGS 84, or null without a boundary or a known projection. */
export function blockRing(block: AreaBlock, projectEpsg: number | null | undefined): LatLng[] | null {
  if (block.deleted_at || !block.boundary || block.boundary.length < 3) return null;
  const epsg = block.epsg ?? projectEpsg ?? null;
  const ring = block.boundary.map(([e, n]) => projectedToLatLng(e, n, epsg));
  return ring.some((p) => p === null) ? null : (ring as LatLng[]);
}

/**
 * A contract area's ring: its grid boundary in its zone, else its printed table (geographic [lon, lat], or grid in
 * the table's zone); WGS 84, GDA 94 and GDA2020 are drawn alike (under 2 m apart). Null without either.
 */
export function contractAreaRing(a: AreaShape): LatLng[] | null {
  const hemisphere = a.hemisphere ?? 'S';
  if (a.boundary && a.boundary.length >= 3 && a.zone) {
    return a.boundary.map(([e, n]) => utmToLatLng(e, n, a.zone!, hemisphere));
  }
  const t = a.boundary_table;
  if (t && t.points.length >= 3) {
    if (t.kind === 'geographic') return t.points.map(([lng, lat]) => ({ lat, lng }));
    const zone = t.zone ?? a.zone;
    if (zone) return t.points.map(([e, n]) => utmToLatLng(e, n, zone, hemisphere));
  }
  return null;
}

const finite = (ring: readonly LatLng[]): boolean =>
  ring.every((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

/**
 * The box of a project's area, as the Operations Hub outlines it: its block boundaries when any can be drawn,
 * otherwise its contract's survey areas; null when neither gives a ring.
 */
export function projectAreaBox(p: {
  readonly epsg: number | null | undefined;
  readonly blocks: readonly AreaBlock[];
  readonly contractAreas: readonly AreaShape[] | null | undefined;
}): GeoBounds | null {
  const blocks = p.blocks
    .map((b) => blockRing(b, p.epsg))
    .filter((r): r is LatLng[] => r !== null && finite(r));
  const rings =
    blocks.length > 0
      ? blocks
      : (p.contractAreas ?? [])
          .map((a) => contractAreaRing(a))
          .filter((r): r is LatLng[] => r !== null && finite(r));
  return boundsOf(rings.flat());
}
